import "./routing-guide-layout.css";

import { useMemo } from "react";

import { LoadingSpinner } from "~/shared/components/loading-spinner/loading-spinner.tsx";
import { TraxModal } from "~/shared/components/trax-modal/trax-modal.tsx";
import { TraxPageHeader } from "~/shared/components/trax-page-header/trax-page-header.tsx";
import { TraxPageLayout } from "~/shared/components/trax-page-layout/trax-page-layout.tsx";
import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import { useIsAuthenticated } from "~/shared/utils/use-is-authenticated.tsx";

import { RoutingGuideGrid } from "../components/grid/index.tsx";
import { RoutingGuideModal } from "../components/modal/index.tsx";
import { initialGuide } from "../components/modal/utils/constants.ts";
import { RoutingGuideActionButtons } from "../components/routing-guide-action-buttons/routing-guide-action-buttons.tsx";
import { RoutingGuideHeader } from "../components/routing-guide-header/routing-guide-header.tsx";
import { SimulateQuoteModal } from "../components/simulate/modal/index.tsx";
import { useGetGlobalRoutingGuides } from "../hooks/use-get-global-routing-guides.ts";
import { useRoutingGuideLayout } from "../hooks/use-routing-guide-layout.ts";

export const GlobalLayout = () => {
	const { isInternal } = useDeploymentType();

	const { accountRoles } = useIsAuthenticated();

	const canEdit = isInternal && accountRoles.includes("LTLAdmin");

	const {
		globalRoutingGuides,
		globalRoutingGuidesIsLoading,
		globalRoutingGuidesIsFetching,
	} = useGetGlobalRoutingGuides();

	const {
		activeCustomer,
		isCustomerLoading,
		activeGuideId,
		showRoutingGuideModal,
		handleOpenEditModal,
		onClose,
		handleCustomerChange,
		handleAddNew,
		requestDelete,
		confirmDelete,
		cancelDelete,
		pendingDeleteId,
		showSimulateModal,
		handleOpenSimulateModal,
		handleCloseSimulateModal,
	} = useRoutingGuideLayout();

	const pendingDeleteGuide = useMemo(
		() =>
			pendingDeleteId === null
				? undefined
				: (globalRoutingGuides ?? []).find((g) => g.id === pendingDeleteId),
		[pendingDeleteId, globalRoutingGuides],
	);

	const selectedGuide = useMemo(() => {
		const found = (globalRoutingGuides ?? []).find(
			(guide) => guide.id === activeGuideId,
		);
		if (found) return found;
		if (activeGuideId === null) return null;
		return { ...initialGuide, id: activeGuideId, customerId: null };
	}, [globalRoutingGuides, activeGuideId]);

	return (
		<TraxPageLayout>
			<SimulateQuoteModal
				handleClose={handleCloseSimulateModal}
				showSimulateModal={showSimulateModal}
			/>
			<TraxPageHeader title={"Global Routing Guide"} />
			<div className={"routing-guide-layout-content"}>
				<RoutingGuideHeader
					onCustomerChange={handleCustomerChange}
					selectedCustomer={activeCustomer}
				/>
				{isCustomerLoading ? (
					<LoadingSpinner />
				) : (
					<div className={"routing-guide-layout-grid-section"}>
						<div className={"routing-guide-layout-grid-wrapper"}>
							<RoutingGuideGrid
								canEdit={canEdit}
								dataState={globalRoutingGuides ?? []}
								isLoading={
									globalRoutingGuidesIsLoading || globalRoutingGuidesIsFetching
								}
								onHandleDelete={(id) => {
									requestDelete(id);
								}}
								onHandleOpenEditModal={handleOpenEditModal}
							/>
						</div>
						{canEdit ? (
							<RoutingGuideActionButtons
								addNewLabel={"Add New Global Rule Set"}
								onAddNew={handleAddNew}
								onOpenSimulateModal={handleOpenSimulateModal}
							/>
						) : null}
					</div>
				)}
			</div>
			{showRoutingGuideModal && canEdit ? (
				<RoutingGuideModal onClose={onClose} routingGuide={selectedGuide} />
			) : null}
			{pendingDeleteId === null ? null : (
				<TraxModal
					showConfirmationButtons
					cancelText={"Cancel"}
					confirmText={"Delete"}
					onCancel={() => {
						cancelDelete();
					}}
					onClose={() => {
						cancelDelete();
					}}
					onConfirm={() => {
						confirmDelete();
					}}
					title={"Delete Routing Guide"}
					type={"confirmation"}
				>
					Are you sure you want to delete{" "}
					<strong>{pendingDeleteGuide?.name ?? "this routing guide"}</strong>?
					This action cannot be undone.
				</TraxModal>
			)}
		</TraxPageLayout>
	);
};
